import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight, ChevronLeft, CalendarDays, Phone, CheckCircle2, ArrowRight, Home, Users, Footprints, ShieldAlert, Bath, Moon, ArmchairIcon, Accessibility, Heart, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { computeFinderResult, FAQ_ITEMS, RATGEBER_SECTIONS } from "@/data/pflegeleistungenData";

const ICON_URL = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/72f9n7x9_1775301634776.png";
const TOTAL_STEPS = 10;

const STEP_CONFIG = [
  { key: "pflegegrad", title: "Welcher Pflegegrad liegt vor?", sub: "Der Pflegegrad bestimmt, welche Leistungen Ihnen zustehen.",
    options: [
      { value: "0", label: "Kein Pflegegrad" }, { value: "1", label: "Pflegegrad 1" }, { value: "2", label: "Pflegegrad 2" },
      { value: "3", label: "Pflegegrad 3" }, { value: "4", label: "Pflegegrad 4" }, { value: "5", label: "Pflegegrad 5" },
    ], cols: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" },
  { key: "wohnsituation", title: "Wo lebt die pflegebedürftige Person?", sub: "Ihre Wohnsituation beeinflusst die Empfehlungen.",
    options: [
      { value: "allein", label: "Allein" }, { value: "partner", label: "Mit Partner" }, { value: "familie", label: "Mit Familie" },
      { value: "betreut", label: "Betreutes Wohnen" }, { value: "einrichtung", label: "Pflegeeinrichtung" },
    ], cols: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" },
  { key: "mobilitaet", title: "Bestehen Mobilitätseinschränkungen?", sub: "Wie gut kann sich die Person selbstständig bewegen?",
    options: [
      { value: "keine", label: "Keine" }, { value: "leicht", label: "Leicht" }, { value: "mittel", label: "Mittel" }, { value: "stark", label: "Stark" },
    ], cols: "grid-cols-2 sm:grid-cols-4" },
  { key: "treppen", title: "Gibt es Probleme beim Treppensteigen?", sub: "", type: "yesno" },
  { key: "bad", title: "Gibt es Probleme beim Duschen oder Baden?", sub: "", type: "yesno" },
  { key: "sturz", title: "Besteht Sturzgefahr?", sub: "Z. B. durch Unsicherheit beim Gehen, Schwindel oder Gleichgewichtsprobleme.", type: "yesno" },
  { key: "nacht", title: "Ist nachts häufig Hilfe erforderlich?", sub: "Z. B. Toilettengang, Lagern oder Beruhigung.", type: "yesno" },
  { key: "aufstehen", title: "Ist das Aufstehen aus Bett oder Sessel erschwert?", sub: "", type: "yesno" },
  { key: "rollstuhl", title: "Ist ein Rollstuhl vorhanden?", sub: "", type: "yesno" },
  { key: "pflegeart", title: "Wer übernimmt die Pflege?", sub: "Dies beeinflusst die Leistungsempfehlungen.",
    options: [
      { value: "angehoerige", label: "Angehörige" }, { value: "ambulant", label: "Ambulanter Dienst" },
      { value: "kombination", label: "Kombination" }, { value: "niemand", label: "Niemand / unklar" },
    ], cols: "grid-cols-2 sm:grid-cols-4" },
];

export default function PflegeleistungenFinder() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ pflegegrad:"", wohnsituation:"", mobilitaet:"", treppen:"", bad:"", sturz:"", nacht:"", aufstehen:"", rollstuhl:"", pflegeart:"" });
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Pflegeleistungs-Finder: Welche Leistungen stehen Ihnen zu? | Lehnen & Weinacht";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Pflegeleistungs-Finder: Finden Sie heraus, welche Pflegeleistungen, Hilfsmittel, Zuschüsse und Beratungsangebote Ihnen zustehen. Kostenlose Orientierung in wenigen Minuten.");
  }, []);

  const cfg = STEP_CONFIG[step - 1];
  const canNext = () => form[cfg.key] !== "";
  const progress = showResult ? 100 : Math.round(((step - 1) / TOTAL_STEPS) * 100);

  const handleSelect = (val) => setForm({ ...form, [cfg.key]: val });
  const handleNext = () => {
    if (step < TOTAL_STEPS) { setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
    else {
      setResult(computeFinderResult(form));
      setShowResult(true);
      setTimeout(() => document.getElementById("ergebnis")?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };
  const handleBack = () => { if (step > 1) { setStep(step - 1); window.scrollTo({ top: 0, behavior: "smooth" }); } };
  const handleReset = () => {
    setShowResult(false); setResult(null); setStep(1);
    setForm({ pflegegrad:"", wohnsituation:"", mobilitaet:"", treppen:"", bad:"", sturz:"", nacht:"", aufstehen:"", rollstuhl:"", pflegeart:"" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="pl-finder">
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="h-9 w-9 shrink-0 overflow-hidden"><img src={ICON_URL} alt="Logo" className="h-full w-auto object-cover object-left scale-[2.3] origin-[15%_41%]" /></div>
            <div><span className="text-sm font-bold text-slate-800 font-['Manrope'] block leading-tight">Lehnen & Weinacht</span><span className="text-[9px] text-cyan-600 font-semibold tracking-wider uppercase">Pflegeberatung & Alltagsbegleitung</span></div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/blog" className="text-sm text-slate-500 hover:text-cyan-700 transition-colors hidden sm:inline">Ratgeber</Link>
            <a href="/#termin-buchen" className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-all active:scale-95">Termin buchen</a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8">
          <Link to="/" className="hover:text-cyan-700 transition-colors">Startseite</Link><ChevronRight className="h-3 w-3" />
          <Link to="/blog" className="hover:text-cyan-700 transition-colors">Ratgeber</Link><ChevronRight className="h-3 w-3" />
          <span className="text-slate-600">Pflegeleistungs-Finder</span>
        </nav>

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 bg-cyan-50 px-4 py-1.5 rounded-full mb-4"><Search className="h-4 w-4" />Leistungs-Finder</div>
          <h1 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-slate-900 leading-tight mb-3">Pflegeleistungs-Finder</h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">Finden Sie in wenigen Minuten heraus, welche Pflegeleistungen, Hilfsmittel und Zuschüsse für Ihre Situation infrage kommen.</p>
        </div>

        {/* Progress */}
        {!showResult && (
          <div className="mb-8" data-testid="pl-progress">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">Frage {step} von {TOTAL_STEPS}</span>
              <span className="text-sm font-medium text-cyan-700">{progress}%</span>
            </div>
            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-600 to-green-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* Steps */}
        {!showResult && (
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-lg shadow-cyan-900/5 p-6 sm:p-8 mb-10" data-testid="pl-form">
            <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">{cfg.title}</h2>
            {cfg.sub && <p className="text-sm text-slate-500 mb-6">{cfg.sub}</p>}
            {!cfg.sub && <div className="mb-6" />}

            {cfg.type === "yesno" ? (
              <div className="grid grid-cols-2 gap-3 max-w-sm" data-testid={`pl-step-${step}`}>
                {[{ value: "ja", label: "Ja" }, { value: "nein", label: "Nein" }].map(opt => (
                  <button key={opt.value} type="button" data-testid={`pl-${cfg.key}-${opt.value}`}
                    onClick={() => handleSelect(opt.value)}
                    className={`p-5 rounded-xl border-2 text-center transition-all active:scale-[0.97] text-xl font-bold ${form[cfg.key] === opt.value ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200 text-cyan-700" : "border-slate-200 hover:border-cyan-200 text-slate-700"}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className={`grid ${cfg.cols} gap-3`} data-testid={`pl-step-${step}`}>
                {cfg.options.map(opt => (
                  <button key={opt.value} type="button" data-testid={`pl-${cfg.key}-${opt.value}`}
                    onClick={() => handleSelect(opt.value)}
                    className={`p-4 sm:p-5 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${form[cfg.key] === opt.value ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200" : "border-slate-200 hover:border-cyan-200"}`}>
                    <span className="text-base font-bold text-slate-800">{opt.label}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
              {step > 1 ? (
                <button onClick={handleBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-cyan-700 transition-colors" data-testid="pl-back-btn"><ChevronLeft className="h-4 w-4" /> Zurück</button>
              ) : <div />}
              <Button onClick={handleNext} disabled={!canNext()} data-testid="pl-next-btn"
                className="bg-cyan-700 hover:bg-cyan-600 disabled:bg-slate-300 text-white rounded-xl h-12 px-8 text-base font-semibold shadow-md hover:shadow-lg transition-all">
                {step === TOTAL_STEPS ? <><Search className="h-4 w-4 mr-2" /> Leistungen finden</> : <>Weiter <ChevronRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </div>
          </div>
        )}

        {/* Results */}
        {showResult && result && (
          <div id="ergebnis" data-testid="pl-ergebnis">
            {/* Summary */}
            <div className="bg-white rounded-2xl border border-cyan-200 shadow-lg p-6 sm:p-8 mb-6">
              <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-4">Ihre individuelle Leistungsübersicht</h2>
              <p className="text-base text-slate-600 leading-relaxed mb-2">Auf Basis Ihrer Angaben kommen insbesondere folgende Leistungen und Hilfsmittel infrage:</p>
              {result.pg === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mt-4">
                  <p className="text-sm text-amber-800"><strong>Ohne Pflegegrad</strong> stehen derzeit keine Pflegeleistungen zur Verfügung. Sollte ein Unterstützungsbedarf bestehen, empfehlen wir die Beantragung eines Pflegegrads. Wir beraten Sie gerne dazu.</p>
                </div>
              )}
            </div>

            {/* Pflegeleistungen */}
            {result.pflegeleistungen.length > 0 && (
              <div className="bg-white rounded-2xl border border-green-200 shadow-lg p-6 sm:p-8 mb-6" data-testid="pl-leistungen">
                <div className="flex items-center gap-2 mb-5">
                  <div className="h-10 w-10 bg-green-100 rounded-xl flex items-center justify-center"><Heart className="h-5 w-5 text-green-700" /></div>
                  <h3 className="font-['Manrope'] text-lg font-bold text-slate-900">Pflegeleistungen</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {result.pflegeleistungen.map((l, i) => (
                    <div key={i} className="bg-green-50/50 rounded-xl p-4 border border-green-100">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-slate-800">{l.name}</span>
                        <span className="text-sm font-bold text-green-700">{l.betrag}</span>
                      </div>
                      <p className="text-xs text-slate-500">{l.info}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Wohnumfeld */}
            {result.wohnumfeld.length > 0 && (
              <div className="bg-white rounded-2xl border border-cyan-200 shadow-lg p-6 sm:p-8 mb-6" data-testid="pl-wohnumfeld">
                <div className="flex items-center gap-2 mb-5">
                  <div className="h-10 w-10 bg-cyan-100 rounded-xl flex items-center justify-center"><Home className="h-5 w-5 text-cyan-700" /></div>
                  <div><h3 className="font-['Manrope'] text-lg font-bold text-slate-900">Wohnumfeldverbessernde Maßnahmen</h3><p className="text-xs text-slate-500">Zuschuss der Pflegekasse bis zu 4.180 € pro Maßnahme</p></div>
                </div>
                <div className="space-y-3">
                  {result.wohnumfeld.map((w, i) => (
                    <div key={i} className="bg-cyan-50/50 rounded-xl p-4 border border-cyan-100">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-slate-800">{w.name}</span>
                        <span className="text-xs font-semibold text-cyan-700 bg-cyan-100 px-2 py-0.5 rounded-full">{w.zuschuss}</span>
                      </div>
                      <p className="text-xs text-slate-500">{w.info}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hilfsmittel */}
            {result.hilfsmittel.length > 0 && (
              <div className="bg-white rounded-2xl border border-amber-200 shadow-lg p-6 sm:p-8 mb-6" data-testid="pl-hilfsmittel">
                <div className="flex items-center gap-2 mb-5">
                  <div className="h-10 w-10 bg-amber-100 rounded-xl flex items-center justify-center"><Star className="h-5 w-5 text-amber-700" /></div>
                  <h3 className="font-['Manrope'] text-lg font-bold text-slate-900">Empfohlene Hilfsmittel</h3>
                </div>
                <div className="space-y-3">
                  {result.hilfsmittel.map((h, i) => (
                    <div key={i} className={`rounded-xl p-4 border ${h.highlight ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-100"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-slate-800">{h.name} {h.highlight && <span className="text-xs font-semibold text-white bg-amber-600 px-2 py-0.5 rounded-full ml-2">Empfohlen</span>}</span>
                      </div>
                      <p className="text-xs text-slate-500 mb-1">{h.info}</p>
                      <p className="text-xs text-cyan-700 font-medium">{h.zuschuss}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Beratung */}
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-lg p-6 sm:p-8 mb-6" data-testid="pl-beratung">
              <div className="flex items-center gap-2 mb-5">
                <div className="h-10 w-10 bg-cyan-100 rounded-xl flex items-center justify-center"><Users className="h-5 w-5 text-cyan-700" /></div>
                <h3 className="font-['Manrope'] text-lg font-bold text-slate-900">Beratungsangebote</h3>
              </div>
              <ul className="space-y-2">
                {result.beratung.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 p-3 bg-cyan-50/50 rounded-xl border border-cyan-100">
                    <CheckCircle2 className="h-5 w-5 text-cyan-600 mt-0.5 shrink-0" />
                    <div><span className="text-sm font-bold text-slate-800">{b.name}</span><p className="text-xs text-slate-500">{b.info}</p></div>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-br from-cyan-50 to-green-50 rounded-2xl border border-cyan-100 p-8 mb-8" data-testid="pl-cta">
              <h3 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-3">Benötigen Sie Unterstützung bei der Auswahl oder Beantragung?</h3>
              <p className="text-base text-slate-600 leading-relaxed mb-6">Unsere digitale Pflegeberatung hilft Ihnen, alle Leistungen, Hilfsmittel und Zuschüsse zu identifizieren und zu beantragen — kostenfrei per Video, Telefon oder vor Ort.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="/#termin-buchen" data-testid="pl-cta-termin" className="inline-flex items-center justify-center gap-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]"><CalendarDays className="h-5 w-5" /> Kostenlose Pflegeberatung anfragen</a>
                <a href="tel:+491637917044" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]"><Phone className="h-5 w-5" /> 0163 791 7044</a>
              </div>
            </div>

            <div className="text-center mb-10">
              <Button onClick={handleReset} variant="outline" className="rounded-xl px-8 h-12 text-base" data-testid="pl-reset-btn">Neue Suche starten</Button>
            </div>
          </div>
        )}

        {/* SEO Ratgeber */}
        <section className="mb-12" data-testid="pl-ratgeber">
          <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-8">Ratgeber: Alle Pflegeleistungen im Überblick</h2>
          <div className="prose-custom space-y-8">
            {RATGEBER_SECTIONS.map((s, i) => (
              <div key={i}>
                <h3 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-3">{s.heading}</h3>
                {s.paragraphs.map((p, j) => <p key={j} className="text-base text-slate-600 leading-relaxed mb-3">{p}</p>)}
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12" data-testid="pl-faq">
          <h2 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-6">Häufige Fragen zu Pflegeleistungen</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-slate-200/60 rounded-xl px-5 shadow-sm">
                <AccordionTrigger className="text-base font-semibold text-slate-800 hover:text-cyan-700 hover:no-underline py-4">{item.q}</AccordionTrigger>
                <AccordionContent className="text-base text-slate-600 leading-relaxed pb-4">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Related */}
        <section className="mb-12">
          <h3 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4">Weiterführende Tools und Artikel</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { slug: "sozialraumbudget-rechner", title: "Sozialraumbudget-Rechner", cat: "Interaktiv" },
              { slug: "verhinderungspflege-antrag", title: "Verhinderungspflege-Antragsassistent", cat: "Interaktiv" },
              { slug: "sozialraumbudget-pflege", title: "Sozialraumbudget in der Pflege", cat: "Sozialraumbudget" },
              { slug: "pflegebegleitung", title: "Pflegebegleitung: Was Angehörige wissen sollten", cat: "Pflegebegleitung" },
            ].map(a => (
              <Link key={a.slug} to={`/blog/${a.slug}`} className="bg-white border border-slate-200/60 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group">
                <span className="text-xs font-medium text-cyan-700 bg-cyan-50 px-2 py-1 rounded-full">{a.cat}</span>
                <p className="text-sm font-semibold text-slate-800 mt-2 group-hover:text-cyan-700 transition-colors">{a.title}</p>
              </Link>
            ))}
          </div>
        </section>

        <div className="bg-slate-100 rounded-xl p-5 mb-10 text-sm text-slate-500 italic">
          Dieser Leistungs-Finder dient der allgemeinen Orientierung und ersetzt keine individuelle Pflegeberatung. Die Angaben basieren auf den aktuellen gesetzlichen Regelungen. Für eine verbindliche Prüfung nutzen Sie bitte unsere kostenfreie Pflegeberatung.
        </div>

        <div className="text-center pt-6 border-t border-slate-200 mb-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-cyan-700 hover:underline font-medium"><ArrowRight className="h-4 w-4 rotate-180" /> Alle Ratgeber-Artikel</Link>
        </div>
      </div>

      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} Lehnen & Weinacht — Pflegeberatung & Alltagsbegleitung</p>
          <div className="flex justify-center gap-6 mt-3 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Ratgeber</Link>
            <a href="/#kontakt" className="hover:text-white transition-colors">Kontakt</a>
          </div>
        </div>
      </footer>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        "mainEntity": FAQ_ITEMS.map(item => ({ "@type": "Question", "name": item.q, "acceptedAnswer": { "@type": "Answer", "text": item.a } }))
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "WebPage",
        "name": "Pflegeleistungs-Finder",
        "description": "Finden Sie heraus, welche Pflegeleistungen, Hilfsmittel und Zuschüsse Ihnen zustehen.",
        "url": "https://lehnen-weinacht-digitalberatung.de/blog/pflegeleistungen-finder",
        "breadcrumb": { "@type": "BreadcrumbList", "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://lehnen-weinacht-digitalberatung.de/" },
          { "@type": "ListItem", "position": 2, "name": "Ratgeber", "item": "https://lehnen-weinacht-digitalberatung.de/blog" },
          { "@type": "ListItem", "position": 3, "name": "Pflegeleistungs-Finder" }
        ]}
      }) }} />
    </div>
  );
}
